import { MockServiceStore } from "./MockServiceStore";

/**
 * StructureCast - Estimate (upstream quoting for a project)
 */
export interface StructureCastEstimate {
  id: string;
  projectId: string;
  estimateNumber: string;
  revisionNumber: number;
  status: "draft" | "submitted" | "approved" | "rejected" | "converted";
  preparedBy: string;
  approvedBy: string;
  approvedDate: string;
  // Rate sheet (unit prices)
  rateConcretePsCy: number;
  rateLaborPerPiece: number;      // Repurposed: Rebar $/20ft
  rateEmbedPerPiece: number;      // Repurposed: Frame & Cover $/ea
  rateLiftInsert: number;
  rateWaterstopPerLf: number;
  rateEngineeringFlat: number;
  rateShippingPerLoad: number;
  rateCranePerDay: number;
  // Rolled-up totals
  totalMaterialCost: number;
  totalLaborCost: number;
  totalEngineeringCost: number;
  totalShippingCost: number;
  grandTotal: number;
  // Logistics
  estimatedLoads: number;
  estimatedCraneDays: number;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export const mockStructureCastEstimates: StructureCastEstimate[] = [
  {
    id: "sc-est-1",
    projectId: "sc-proj-1",
    estimateNumber: "25-1585",
    revisionNumber: 1,
    status: "converted",
    preparedBy: "Office (MSCP)",
    approvedBy: "HRCSD",
    approvedDate: "2025-07-08T00:00:00.000Z",
    rateConcretePsCy: 215,           // $/CY (5,000 PSI Type II/V Low Alkali)
    rateLaborPerPiece: 40,           // Rebar $/20ft (ASTM A-706/A-615 Grade 40/60, Caltrans D73B/G)
    rateEmbedPerPiece: 425,          // Ring & Cover $/ea (1900 Sanitary Sewer CPH frame + cover, HW190023)
    rateLiftInsert: 0,
    rateWaterstopPerLf: 0,
    rateEngineeringFlat: 2500,
    rateShippingPerLoad: 275,        // Santa Maria, CA → Heritage Ranch/Paso Robles (~50 mi up US-101)
    rateCranePerDay: 575,
    // Totals back-filled from line items
    totalMaterialCost: 0,
    totalLaborCost: 0,
    totalEngineeringCost: 2500,
    totalShippingCost: 0,
    grandTotal: 0,
    estimatedLoads: 4,
    estimatedCraneDays: 3,
    notes: "Heritage Ranch WRRF — Job #25-1585. Quote #25-1585. 23 × 48\" sanitary sewer manholes (mix of depths). 5,000 PSI Type II/V Low Alkali, polypropylene fibers (Caltrans secondary reinforcing). CS-102 butyl sealant + Press-Seal cast-in pipe boots. 1900 Sanitary Sewer CPH frame + cover (HW190023). FOB: Mid-State Concrete Products yard, Santa Maria. Delivery to Heritage Ranch (SLO County). San Luis Obispo County sales tax 7.25%. Net 30.",
    createdAt: "2025-05-29T00:00:00.000Z",
    updatedAt: "2025-07-08T00:00:00.000Z",
  },
  {
    id: "sc-est-2",
    projectId: "sc-proj-1",
    estimateNumber: "25-1585-ADD",
    revisionNumber: 2,
    status: "draft",
    preparedBy: "Office (MSCP)",
    approvedBy: "",
    approvedDate: "",
    rateConcretePsCy: 220,           // Q1 2026 escalation
    rateLaborPerPiece: 42,
    rateEmbedPerPiece: 435,
    rateLiftInsert: 0,
    rateWaterstopPerLf: 0,
    rateEngineeringFlat: 1200,
    rateShippingPerLoad: 275,
    rateCranePerDay: 575,
    totalMaterialCost: 0,
    totalLaborCost: 0,
    totalEngineeringCost: 1200,
    totalShippingCost: 0,
    grandTotal: 0,
    estimatedLoads: 1,
    estimatedCraneDays: 1,
    notes: "DRAFT — Add-on for SSMH-10 (Pinion Trail WRRF yard-piping add) and possible add'l 24\" eccentric cones. Pending HRCSD / engineer field approval.",
    createdAt: "2026-01-22T00:00:00.000Z",
    updatedAt: "2026-01-22T00:00:00.000Z",
  },
];

/**
 * Back-fill rolled-up totals for the estimate from line-item data.
 */
export const backfillEstimateTotals = (
  lineItemSubtotals: { totalMaterial: number; totalLabor: number },
  estimate: StructureCastEstimate
): void => {
  estimate.totalMaterialCost = +lineItemSubtotals.totalMaterial.toFixed(2);
  estimate.totalLaborCost = +lineItemSubtotals.totalLabor.toFixed(2);
  estimate.totalShippingCost = +(
    estimate.estimatedLoads * estimate.rateShippingPerLoad +
    estimate.estimatedCraneDays * estimate.rateCranePerDay
  ).toFixed(2);
  estimate.grandTotal = +(
    estimate.totalMaterialCost +
    estimate.totalLaborCost +
    estimate.totalEngineeringCost +
    estimate.totalShippingCost
  ).toFixed(2);
};

export const createMockStructureCastEstimateStore = () =>
  new MockServiceStore<StructureCastEstimate>({
    mockData: mockStructureCastEstimates,
    idField: "id",
    simulateDelay: 200,
    persistKey: "preferred-structurecast-estimates",
  });

export default mockStructureCastEstimates;
